import { createContext, useContext } from "react";
import { useDocumentBranding } from "@/api/queries";
import type { BrandingColour, DocumentBranding } from "@/api/types";

/**
 * The colours a company can print in. These are the colour of its paper, chosen by the company and
 * named in the design, so they're values here and not theme tokens: a printed invoice doesn't follow
 * the app's light or dark mode.
 */
export const PALETTE: Record<BrandingColour, { label: string; hex: string }> = {
  jade: { label: "Jade", hex: "#0c6b5e" },
  ink: { label: "Ink", hex: "#16191b" },
  slate: { label: "Slate", hex: "#3d5a6c" },
  maroon: { label: "Maroon", hex: "#7a2e2a" },
};

export const DEFAULT_BRANDING: DocumentBranding = {
  invoiceTemplate: "classic",
  receiptTemplate: "centred",
  colour: "jade",
  receiptColour: null,
  showLogo: true,
  showTaxNumber: true,
  showContact: true,
  showPaymentDetails: true,
  showAmountInWords: true,
  showSignature: true,
  showDiscount: true,
  logoData: null,
};

/** The branding page's unsaved form, so its preview prints what the form says and not what's saved. */
export const BrandingPreviewContext = createContext<DocumentBranding | null>(null);

/** What a printed document looks like, by whether it's an invoice or a credit note, or a receipt. */
export function usePrintBranding(scope: "invoice" | "receipt") {
  const preview = useContext(BrandingPreviewContext);
  const { data } = useDocumentBranding();
  const branding = preview ?? data ?? DEFAULT_BRANDING;

  const colour =
    scope === "receipt" ? (branding.receiptColour ?? branding.colour) : branding.colour;
  return {
    ...branding,
    template: scope === "receipt" ? branding.receiptTemplate : branding.invoiceTemplate,
    accent: PALETTE[colour].hex,
  };
}
